// src/features/tidsregistering/components/absence-planner/AbsenceDetailDialog.jsx
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, Divider, TextField,
  Alert, CircularProgress, IconButton,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import StaffMember from '../../../../components/common/StaffMember';
import { APPROVAL_CHIP } from '../../constants/absence';
import { useTidsregistreringAbsencePermissions } from '../../../auth/hooks/usePermissions';
import { parseApiTimestamp } from '../../../../utils/formatters/dateFormatter';

export default function AbsenceDetailDialog({
  open,
  slot,
  staffMember,
  onClose,
  onApprove,
  onReject,
  onDelete,
  loading,
}) {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const { canApprove, canDelete } = useTidsregistreringAbsencePermissions();

  if (!slot) return null;

  const { originalAbsence: abs, registrationCode, approvalState } = slot;
  const chip = APPROVAL_CHIP[approvalState] ?? { label: approvalState, color: 'default' };
  const isPending = approvalState === 'approval_wish';
  const isApproved = approvalState === 'approval_wish_approved';

  const handleRejectConfirm = () => {
    onReject(abs.absence_id, { note: rejectNote });
    setRejectNote('');
    setShowRejectInput(false);
  };

  const handleClose = () => {
    setRejectNote('');
    setShowRejectInput(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">{t('tidsregistering:absence_detail_dialog.title')}</Typography>
            <Chip {...chip} size="small" sx={{ mt: 0.5 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {staffMember && (
              <StaffMember staffMember={staffMember} avatarSize={32} gap={1} clickable={false} />
            )}
            <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Code & dates */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('tidsregistering:absence_detail_dialog.absence_type')}</Typography>
            <Typography variant="body1" fontWeight={600}>
              {registrationCode?.title ?? '—'}
            </Typography>
            {registrationCode?.description && (
              <Typography variant="caption" color="text.secondary">{registrationCode.description}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('tidsregistering:absence_detail_dialog.period')}</Typography>
            <Typography variant="body1" fontWeight={600}>
              {abs.start_date} – {abs.end_date ?? t('tidsregistering:absence_detail_dialog.open_ended')}
            </Typography>
          </Box>
          {abs.hours_sum != null && (
            <Box>
              <Typography variant="caption" color="text.secondary">{t('tidsregistering:absence_detail_dialog.hours')}</Typography>
              <Typography variant="body1" fontWeight={600}>{Number(abs.hours_sum).toFixed(1)}t</Typography>
            </Box>
          )}
        </Box>

        {abs.note && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">{t('tidsregistering:absence_detail_dialog.note_from_staff')}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>{abs.note}</Typography>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Approval history */}
        <Typography variant="caption" color="text.secondary">{t('tidsregistering:absence_detail_dialog.approval_history')}</Typography>
        {abs.approval_events?.length > 0 ? (
          <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {abs.approval_events.map((ev, i) => (
              <Typography key={i} variant="caption" display="block">
                {ev.new_value ? `✓ ${t('tidsregistering:absence_detail_dialog.approved')}` : `✗ ${t('tidsregistering:absence_detail_dialog.rejected')}`} — {ev.created_at ? format(parseApiTimestamp(ev.created_at), 'dd/MM/yyyy HH:mm') : ''}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
            {t('tidsregistering:absence_detail_dialog.no_history')}
          </Typography>
        )}

        {/* Reject note input */}
        {showRejectInput && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t('tidsregistering:absence_detail_dialog.reject_reason_label')}
              multiline
              rows={2}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" color="error" variant="contained" onClick={handleRejectConfirm} disabled={loading}>
                {t('tidsregistering:absence_detail_dialog.confirm_rejection')}
              </Button>
              <Button size="small" onClick={() => setShowRejectInput(false)}>{t('common:buttons.cancel')}</Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {!abs.is_system_generated && canDelete && (
            <IconButton
              size="small"
              color="error"
              disabled={loading}
              onClick={() => onDelete(abs.absence_id)}
              title={t('tidsregistering:absence_detail_dialog.delete_absence')}
            >
              {loading ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose}>{t('common:buttons.close')}</Button>
          {isPending && canApprove && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                disabled={loading}
                onClick={() => setShowRejectInput(true)}
              >
                {t('tidsregistering:absence_detail_dialog.reject')}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
                disabled={loading}
                onClick={() => onApprove(abs.absence_id)}
              >
                {t('tidsregistering:absence_detail_dialog.approve')}
              </Button>
            </>
          )}
          {isApproved && canApprove && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              disabled={loading}
              onClick={() => setShowRejectInput(true)}
            >
              {t('tidsregistering:absence_detail_dialog.revoke_approval')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
